<template>
  <div class="stock-chart-wrapper">
    <div ref="chartContainer"></div>
  </div>
</template>

<script>
import Moment from 'moment';
import MomentTimeZone from 'moment-timezone';
import {
  timestampToCETConvertor,
  convertDateToCETDate,
} from '../../utils/helpers.js';

const Highcharts = require('highcharts');

// Load the boost module, and initialize it.
require('highcharts/modules/boost')(Highcharts);
require('highcharts/modules/stock')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);
require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/accessibility')(Highcharts);
require('highcharts/modules/draggable-points')(Highcharts);

export default {
  name: 'StockChart',
  data() {
    return {
      chart: undefined,
      loaded: false,
      chartSeries: null,
      isDraggablePoints: false,
      dragPointsArray: [],
      cloneOfChartSeries: null,
      isDataChangedByDrag: false,
      lastSelectedPointIndex: null,
      currentDragginPoint: null,
      selectedDataPointsArray: [],
      currentMouseOverPointDetails: null,
      currentDragIndex: null,
    };
  },
  props: {
    seriesDetails: Array,
    redrawChart: Boolean,
    enableXAxiesLabels: {
      required: false,
    },
    enableYAxiesLabels: {
      required: false,
    },
    enableTooltip: {
      required: false,
    },
    enableAnimation: {
      required: false,
    },
    lineWidth: {
      required: false,
      default: 1,
    },
    lineColor: {
      required: false,
      default: '#adb9d2',
    },
    axisStyleFontSize: {
      default: '10px',
    },
    enableLegend: {
      default: false,
    },
    xAxisPlotBands: {
      type: Array,
      default: () => [],
    },
    calculateMetaData: {
      type: Function,
      default: () => {},
    },
    addNewSeries: {
      type: Function,
      default: () => {},
    },
    setAndCreateLogObject: {
      type: Function,
      default: () => {},
    },
    isDragUnableOnPoints: {
      type: Boolean,
      default: true,
    },
    timeSeriesData: {
      required: false,
      type: Array,
      default: () => [],
    },
  },
  methods: {
    setChart() {
      const vm = this;
      Highcharts.setOptions({
        lang: {
          months: [
            'Januar',
            'Februar',
            'März',
            'April',
            'Mai',
            'Juni',
            'Juli',
            'August',
            'September',
            'Oktober',
            'November',
            'Dezember',
          ],
          weekdays: [
            'Sonntag',
            'Montag',
            'Dienstag',
            'Mittwoch',
            'Donnerstag',
            'Freitag',
            'Samstag',
          ],
          shortMonths: [
            'Jan',
            'Feb',
            'Mär',
            'Apr',
            'Mai',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Okt',
            'Nov',
            'Dez',
          ],
          decimalPoint: ',',
          thousandsSep: '.',
          rangeSelectorFrom: 'Von',
          rangeSelectorTo: 'Bis',
          rangeSelectorZoom: 'Zeitraum',
          resetZoom: 'Zoom zurücksetzen',
          resetZoomTitle: 'Zoom zurücksetzen',
          downloadPNG: 'Download als PNG',
          downloadJPEG: 'Download als JPEG',
          downloadPDF: 'Download als PDF',
          downloadSVG: 'Download als SVG',
          printChart: 'Drucken',
          noData: 'Keine Daten vorhanden',
        },
        time: {
          timezone: 'Europe/Berlin',
        },
      });
      this.chart = Highcharts.stockChart(this.$refs.chartContainer, {
        timezone: 'Europe/Berlin',
        chart: {
          type: 'line',
          height: 450,
          zoomType: 'x',
          animation: this.enableAnimation,
          events: {
            selection: function (event) {
              //when chart gets selection range(zoom change) check if dots and dragging are active or not
              let min, max;
              // Get min & max from xAxis
              if (event.hasOwnProperty('xAxis')) {
                min = event.xAxis[0].min;
                max = event.xAxis[0].max;
              } else {
                // reset values
                min = null;
                max = null;
              }

              // Emit to parent
              vm.$emit('setSelectionRange', min, max);
            },
          },
        },
        rangeSelector: {
          // inputEnabled: false,

          selected: 6,
          buttons: [
            {
              type: 'day',
              count: 3,
              text: '3t',
            },
            {
              type: 'day',
              count: 5,
              text: '5t',
            },
            {
              type: 'week',
              count: 1,
              text: '1w',
            },
            {
              type: 'month',
              count: 1,
              text: '1m',
            },
            {
              type: 'month',
              count: 6,
              text: '6m',
            },
            {
              type: 'year',
              count: 1,
              text: '1j',
            },
            {
              type: 'all',
              text: 'Alles',
            },
          ],
          inputStyle: {
            color: '#039',
            outline: 'none',
            padding: '5px',
            'justify-content': 'center',
            'align-items': 'center',
            display: 'flex',
          },
        },

        xAxis: {
          min: this.min,
          max: this.max,
          crosshair: {
            color: vm.isDragUnableOnPoints ? 'transparent' : 'grey', // Set the crosshair line color to transparent by default,
          },
          // plotBands: this.xAxisPlotBands,
          type: 'datetime',
          // labels: {
          //   enabled: this.enableXAxiesLabels,
          //   style: this.axisStyleFontSize,
          // },
          events: {
            afterSetExtremes: function (event) {
              this.chart.redraw();
              vm.checkAndAddDragPointInChart(event, vm.chart);
              if (vm.selectedDataPointsArray.length > 0)
                vm.selectAllDataPoint(
                  event.target.chart.series[0],
                  vm.selectedDataPointsArray
                );
              vm.$emit('setSelectionRange', event.min, event.max);
            },
          },
        },
        yAxis: {
          title: null,
          opposite: false,
          offset: 5, // Increase the offset value as needed
          // labels: {
          //   enabled: this.enableYAxiesLabels,
          //   style: {
          //     fontSize: this.axisStyleFontSize,
          //   },
          // },
        },
        credits: {
          enabled: false,
        },
        tooltip: {
          enabled: this.enableTooltip,
          useHTML: true,
          split: false,
          shared: false,
          borderWidth: 2,
          formatter() {
            if (
              vm.currentDragginPoint == null ||
              vm.currentDragginPoint == vm.currentMouseOverPointDetails.x ||
              vm.selectedDataPointsArray.includes(
                vm.currentMouseOverPointDetails.x
              )
            ) {
              const tooltipDetails = vm.getTooltipDetails(
                vm.currentMouseOverPointDetails.x
              );

              return (
                '<b>' +
                'Zeitstempel: ' +
                timestampToCETConvertor(
                  vm.currentMouseOverPointDetails.x,
                  'DD.MM.YY HH:mm'
                ) +
                '<br>Kalenderwoche: ' +
                tooltipDetails.totalWeekNumber.toLocaleString('de-DE') +
                '<br>Tag: ' +
                tooltipDetails.chartHoverDay +
                '<br>Wert: ' +
                vm.currentMouseOverPointDetails.y.toLocaleString('de-DE') +
                '</b>'
              );
            } else {
              {
                return false;
              }
            }
          },
          positioner:
            vm.isDragUnableOnPoints == false &&
            function (labelWidth, labelHeight, point) {
              //Note :- chat tooltip position should active only on series compare time
              let chart = this.chart;
              let tooltipX, tooltipY;
              //this is a coodiotion to check chart is comparing the series or not
              if (vm.isDragUnableOnPoints == false) {
                tooltipX = vm.currentMouseOverPointDetails.plotX;
                tooltipY = vm.currentMouseOverPointDetails.plotY;
                if (tooltipX + labelWidth > chart.chartWidth) {
                  tooltipX -= labelWidth;
                }
                return {
                  x: tooltipX,
                  y: tooltipY,
                };
              } else {
                tooltipX = point.plotX + chart.plotLeft + 20;
                tooltipY = point.plotY + chart.plotTop;

                // Check if there is enough space on the right
                if (tooltipX + labelWidth > chart.chartWidth) {
                  tooltipX -= labelWidth - 40;
                }

                // Check if there is enough space on the bottom
                if (tooltipY + labelHeight > chart.chartHeight) {
                  tooltipY -= labelHeight;
                }

                // Check if there is enough space on the left
                if (tooltipX < chart.plotLeft) {
                  tooltipX = chart.plotLeft;
                }

                // Check if there is enough space on the top
                if (tooltipY < chart.plotTop) {
                  tooltipY = chart.plotTop;
                }

                return {
                  x: tooltipX,
                  y: tooltipY,
                };
              }
            },
        },
        // title: {
        //   text: this.chartSeries[0].name,
        //   align: 'left',
        // },

        legend: {
          enabled: this.enableLegend,
        },
        navigator: {
          adaptToUpdatedData: true, // Disable automatic adaptation of navigator to updated data
        },
        scrollbar: {
          liveRedraw: true,
        },
        plotOptions: {
          series: {
            dataGrouping: {
              enabled: false,
              // groupPixelWidth: 0.1,
            },
            marker: {
              radius: 4,
            },
            turboThreshold: 1000000,
            showInNavigator: true,
            stickyTracking: false,
            allowPointSelect: false,
            lineWidth: this.lineWidth,
            states: {
              hover: {
                lineWidth: this.lineWidth,
              },
            },
            boostThreshold: 1,
            point: {
              events: {
                select: function (event) {
                  vm.currentDragginPoint = null;
                  //there is a seperect data variabe to note down which data point gets selected
                  vm.addSelectedDataPointToArray(this.options.x);

                  //it will add for the grouping data points
                  this.update({
                    groupId: 'edit',
                    marker: {
                      enabled: true,
                    },
                  });
                },
                unselect: function () {
                  //remove when remove from grouping
                  this.update({
                    groupId: null,
                  });
                },
                click: function (event) {
                  if (
                    event.shiftKey &&
                    (event.ctrlKey || event.metaKey) &&
                    vm.lastSelectedPointIndex
                  ) {
                    //this condition for to check if cntl and shift is pressed
                    vm.selectOrUnselectPointsBetweenRange(
                      event.point.options.x,
                      event.point.selected,
                      event.point.series.data
                    );
                  } else if (
                    event.shiftKey ^ event.ctrlKey ^ event.metaKey &&
                    !(event.shiftKey && (event.ctrlKey || event.metaKey))
                  ) {
                    //this condtion for to check only one key is pressed or not
                    //if yes then and point event is unselect then we have to remove from array
                    if (event.point.selected)
                      event.point.marker = { enabled: false };
                    vm.removeSelectedDataPointfromArray(event.point.options.x);
                  } else if (
                    !event.shiftKey &&
                    !event.ctrlKey &&
                    !event.metaKey
                  ) {
                    //if neither key is pressed then we have to unselect other points
                    vm.unSelectAllDataPoint(
                      event.point.series,
                      event.point.options.x
                    );
                  }
                  vm.lastSelectedPointIndex = event.point.options.x;
                },
                mouseOver: function () {
                  //this condition for the tooltip and Yaxis stright line
                  vm.currentMouseOverPointDetails = this;
                  if (vm.isDragUnableOnPoints != false) {
                    if (
                      vm.currentDragginPoint == null ||
                      vm.currentDragginPoint == this.options.x ||
                      vm.selectedDataPointsArray.includes(this.options.x)
                    ) {
                      if (
                        this.series.chart.xAxis[0].crosshair.color != 'gray' &&
                        this.series.chart.series[0].closestPointRangePx > 10
                      ) {
                        this?.series?.chart.xAxis[0].update({
                          crosshair: {
                            color: 'gray', // Set the crosshair line color to gray for the currently dragging point
                          },
                        });
                      }
                      return this;
                    } else {
                      if (
                        this.series?.chart.xAxis[0].crosshair.color == 'gray' &&
                        this.series.chart.series[0]?.closestPointRangePx > 6
                      ) {
                        this?.series?.chart.xAxis[0].update({
                          crosshair: {
                            color: 'transparent', // Set the crosshair line color to gray for the currently dragging point
                          },
                        });
                      }
                      return false;
                    }
                  }
                },
                mouseOut: function () {
                  //hide tooltip on mouseout on marker(data point)
                  this.series.chart.tooltip.hide();
                  vm.currentMouseOverPointDetails = null;
                },
                drop: function (event) {
                  vm.currentDragginPoint = null;
                  // get new values from Drag and it will be object of objects
                  const newDragPoints = Object.entries(event.newPoints).reduce(
                    (previous, [objectKey, objectValue]) => [
                      ...previous,
                      objectValue.point.options,
                    ],
                    []
                  );

                  const newDragPointKeys = newDragPoints.map(
                    (point) => point.x
                  );
                  //store new drag points value and also if point gets update again then remove it and again concat that
                  vm.dragPointsArray = vm.dragPointsArray
                    .filter((point) => !newDragPointKeys.includes(point.x))
                    .concat(newDragPoints);

                  // add overlay on chart calculation and add save buttons for the drag new points
                  if (!vm.isDataChangedByDrag) {
                    vm.$emit('setOperationOverlay', true);
                    vm.toggleDataChangeByDrag(true);
                    vm.addDragButtonsToChart();
                  }
                },
                drag: function () {
                  //when drag start we have to check if unselected points gets drag or not
                  //if yes unselect other point and select dragging point
                  if (!vm.selectedDataPointsArray.includes(this.options.x)) {
                    vm.unSelectAllDataPoint(this.series, this.options.x);
                  }
                  if (!this.selected) {
                    vm.addSelectedDataPointToArray(this.options.x);
                    this.update({
                      groupId: 'edit',
                    });
                    vm.lastSelectedPointIndex = this.options.x;
                    this.selected = true;
                  }
                },
                dragStart: function () {
                  // disable tooltip
                  vm.currentDragginPoint = this.options.x;
                },
              },
            },
          },
          area: {
            fillColor: {
              linearGradient: {
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1,
              },
              stops: [
                [0, Highcharts.Color('#adb9d2').setOpacity(1).get('rgba')],
                [1, Highcharts.Color('#adb9d2').setOpacity(0).get('rgba')],
              ],
            },
            animation: this.enableAnimation,
            lineWidth: this.lineWidth,
            states: {
              hover: {
                lineWidth: this.lineWidth,
              },
            },
            threshold: null,
          },
        },
        navigation: {
          buttonOptions: {
            enabled: false,
          },
        },
        series: this.getChartSeries(),
      });
      this.loaded = true;
    },
    redraw() {
      this.chart.series[0].setData(
        JSON.parse(JSON.stringify(this.seriesDetails[0].data)),
        true
      );
      this.cloneOfChartSeries = JSON.parse(
        JSON.stringify(this.seriesDetails[0].data)
      );
    },
    updateChartWithPlotBands(newValues) {
      const addEventsToPlotBonds = newValues.map((plotBand) => ({
        ...plotBand,
        events: {
          click: function () {
            this.axis.chart.xAxis[0].setExtremes(plotBand.from, plotBand.to);
            this.axis.chart.showResetZoom();
          },
        },
      }));
      this.chart.update({
        xAxis: {
          plotBands: addEventsToPlotBonds,
        },
      });
    },
    addDraggingToChart(chart) {
      if (chart && !this.isDraggablePoints && this.isDragUnableOnPoints) {
        chart.update({
          plotOptions: {
            series: {
              allowPointSelect: true,
              dataGrouping: {
                enabled: true,
              },
              dragDrop: {
                draggableY: true,
                groupBy: 'groupId',
              },
              marker: {
                fillColor: '#0a81f7',
              },
            },
          },
        });
        this.isDraggablePoints = true;
      } else if (chart) {
        chart.update({
          plotOptions: {
            allowPointSelect: true,
            series: {
              dataGrouping: {
                enabled: true,
              },
              marker: {
                fillColor: '#0a81f7',
              },
            },
          },
        });
        this.isDraggablePoints = true;
      }
    },
    removeDraggingToChart(chart) {
      if (chart && this.isDraggablePoints && this.isDragUnableOnPoints) {
        chart.update({
          plotOptions: {
            allowPointSelect: false,
            series: {
              dataGrouping: {
                enabled: false,
              },
              dragDrop: {
                draggableY: false,
              },
              marker: {
                fillColor: undefined,
              },
            },
          },
        });
        this.isDraggablePoints = false;
      } else if (chart) {
        chart.update({
          plotOptions: {
            allowPointSelect: false,
            series: {
              dataGrouping: {
                enabled: false,
              },
              marker: {
                fillColor: undefined,
              },
            },
          },
        });
        this.isDraggablePoints = false;
      }
    },
    checkAndAddDragPointInChart(event, chart) {
      setTimeout(() => {
        const differenceBetweenDatesInDay = Moment(
          event.target.chart.xAxis[0].max
        ).diff(Moment(event.target.chart.xAxis[0].min), 'days');
        if (differenceBetweenDatesInDay < 6) {
          this.addDraggingToChart(chart);
        } else {
          this.removeDraggingToChart(chart);
        }
      }, 500);
    },
    addDragButtonsToChart() {
      //add custom buttons for the save or reset chart drag values
      if (this.chart) {
        this.chart.update({
          navigation: {
            buttonOptions: {
              enabled: true,
            },
          },
          exporting: {
            buttons: [
              {
                text: 'Anpassung übernehmen',
                onclick: () => this.saveChartWithDragValue(),
                verticalAlign: 'top',
                y: -5,
                x: -425,
                theme: {
                  height: 20,
                  'stroke-width': 1,
                  stroke: '#cccccc',
                },
              },
              {
                text: 'Anpassung verwerfen',
                onclick: () => this.resetChartWithoutDragValue(),
                verticalAlign: 'top',
                y: -5,
                x: -280,
                theme: {
                  height: 20,
                  'stroke-width': 1,
                  stroke: '#cccccc',
                },
              },
            ],
          },
        });
      }
    },
    removeDragButtonsToChart() {
      //remove buttons from the chart
      if (this.chart) {
        this.chart.update({
          exporting: {
            buttons: [],
          },
          navigation: {
            buttonOptions: {
              enabled: false,
            },
          },
        });
      }
    },
    resetChartWithoutDragValue() {
      //reset chart with the original value
      if (this.chart) {
        this.chart.series[0].update(
          {
            data: [...this.cloneOfChartSeries],
          },
          false
        );
        //reset drag points array
        this.dragPointsArray = [];
        //toggle overlay above the calculation
        this.$emit('setOperationOverlay', false);
        this.toggleDataChangeByDrag(false);
        this.chart.redraw();
        this.removeDragButtonsToChart();
      }
    },
    async saveChartWithDragValue() {
      const oldNewDataPointsValueForLog = [];
      if (this.chart) {
        const newDragPointKeys = this.dragPointsArray.map((point) => point.x);
        //get new chart data from the original data and dragged point data
        const newChartData = this.cloneOfChartSeries.map((dataPoint) => {
          if (newDragPointKeys.includes(dataPoint[0])) {
            const newDataPointValue = this.dragPointsArray.find(
              (draggedPoint) => draggedPoint.x === dataPoint[0]
            ).y;
            oldNewDataPointsValueForLog.push({
              timestamp: dataPoint[0],
              'old-value': dataPoint[1],
              'new-value': newDataPointValue,
            });
            return [dataPoint[0], Number(newDataPointValue.toFixed(10))];
          } else return dataPoint;
        });
        //calculate metadata for the new chart data
        const calculateMetadataResponse = await this.calculateMetaData({
          ...this.chartSeries[0],
          data: newChartData,
        });
        if (calculateMetadataResponse) {
          //increse the index for  new series
          this.addNewSeries({
            ...calculateMetadataResponse,
            data: newChartData,
          });
          //take a new clone of new chart data for the redo undo functionality
          this.cloneOfChartSeries = [...newChartData];

          //set new datapoint drag data for the logs on save
          this.$emit('setDragDataPoints', oldNewDataPointsValueForLog);
          this.dragPointsArray = [];
          this.$emit('setOperationOverlay', false);
          this.toggleDataChangeByDrag(false);
          this.removeDragButtonsToChart();
        } else {
          this.$emit('setOperationOverlay', false);
        }
      }
    },
    toggleDataChangeByDrag(dragValue) {
      this.isDataChangedByDrag = dragValue;
    },
    selectOrUnselectPointsBetweenRange(nextPoint, isSelected, seriesData) {
      if (isSelected) this.removeSelectedDataPointfromArray(nextPoint);
      for (const dataPoint of seriesData) {
        if (
          dataPoint &&
          ((dataPoint.options.x >= this.lastSelectedPointIndex &&
            dataPoint.options.x < nextPoint) ||
            (dataPoint.options.x <= this.lastSelectedPointIndex &&
              dataPoint.options.x > nextPoint))
        ) {
          if (dataPoint.selected) {
            dataPoint.marker = {
              enabled: false,
            };
          }
          dataPoint.selected = !isSelected;
          if (!isSelected) {
            this.addSelectedDataPointToArray(dataPoint.options.x);
            dataPoint.options = {
              ...dataPoint.options,
              selected: true,
              groupId: 'edit',
            };

            dataPoint.marker = {
              enabled: true,
            };
            dataPoint.groupId = 'edit';
          } else {
            this.removeSelectedDataPointfromArray(dataPoint.options.x);
            dataPoint.options = {
              x: dataPoint.options.x,
              y: dataPoint.options.y,
            };
            dataPoint.marker = {
              enabled: false,
            };
          }
        }
      }
      this.lastSelectedPointIndex = nextPoint;
      if (isSelected) this.lastSelectedPointIndex = null;
    },
    addSelectedDataPointToArray(dataPoint) {
      if (!this.selectedDataPointsArray.includes(dataPoint))
        this.selectedDataPointsArray.push(dataPoint);
    },
    removeSelectedDataPointfromArray(dataPoint) {
      if (this.selectedDataPointsArray.includes(dataPoint))
        this.selectedDataPointsArray = this.selectedDataPointsArray.filter(
          (data) => data != dataPoint
        );
    },
    unSelectAllDataPoint(series, currentPoint) {
      series.data.forEach((data) => {
        if (data.options.x != currentPoint) {
          this.removeSelectedDataPointfromArray(data.options.x);
          if (data.selected) {
            data.marker = {
              enabled: false,
            };
          }
          data.selected = false;
          data.options = {
            x: data.options.x,
            y: data.options.y,
          };
        }
      });
    },
    selectAllDataPoint(series, selectedDataPointsArray) {
      setTimeout(() => {
        series.data.forEach((data) => {
          if (data && selectedDataPointsArray.includes(data.x)) {
            data.marker = {
              enabled: true,
            };

            data.groupId = 'edit';
            data.selected = true;
            data.options = {
              ...data.options,
              selected: true,
              groupId: 'edit',
            };
          }
        });
        this.chart.redraw();
      }, 500);
    },
    getTooltipDetails(date) {
      const weekDays = [
        'Sonntag',
        'Montag',
        'Dienstag',
        'Mittwoch',
        'Donnerstag',
        'Freitag',
        'Samstag',
      ];
      const chartHoverDate = convertDateToCETDate(date);
      const chartHoverDay = weekDays[chartHoverDate.day()];
      const totalWeekNumber = chartHoverDate.isoWeek();
      return {
        chartHoverDay,
        totalWeekNumber,
      };
    },
    getChartSeries() {
      if (
        String(this.chartSeries?.[0]?.ts_type || '')
          .toLowerCase()
          .includes('loadcurvecustom')
      ) {
        const customSeries = JSON.parse(JSON.stringify(this.chartSeries[0]));
        // Create an array to hold the result
        const result = [];

        // Iterate over the columns, skipping the "date" column
        for (let i = 1; i < customSeries.columns.length; i++) {
          const columnName = customSeries.columns[i];
          if (typeof customSeries.data[0][i] !== 'string') {
            // Create an object for the current column
            const columnData = {
              name: columnName,
              data: [],
            };

            // Populate the data array with [date, value] pairs
            for (let j = 0; j < customSeries.data.length; j++) {
              const row = customSeries.data[j];
              const date = row[0];
              const value = row[i];
              columnData.data.push([date, value]);
            }

            // Add the columnData object to the result array
            result.push(columnData);
          }
        }
        return result;
      }
      return [...this.chartSeries];
    },
  },
  computed: {
    liveChart() {
      return this.chart.yAxis[0];
    },
    min() {
      let dates = [];
      for (let series of this.seriesDetails) {
        if (series.data) {
          dates.push(series.data[0][0]);
        }
      }
      const minNumber = Math.min(...dates);
      this.$emit('setMinimumChartValue', minNumber);
      return minNumber;
    },
    max() {
      let dates = [];
      for (let series of this.seriesDetails) {
        if (series.data) {
          let lengthSeries = series.data.length;
          dates.push(series.data[lengthSeries - 1][0]);
        }
      }
      const maxNumber = Math.max(...dates);
      this.$emit('setMaximumChartValue', maxNumber);
      return Number(maxNumber);
    },
  },
  created() {
    window.moment = Moment;
    MomentTimeZone();
  },
  mounted() {
    // Make sure chart input is always array
    //Make sure you clone the original chart data because highchart will update your data which you pass in chart series
    if (!Array.isArray(this.seriesDetails)) {
      this.cloneOfChartSeries = [...this.seriesDetails.data];
      this.chartSeries = [
        { ...this.seriesDetails, data: [...this.cloneOfChartSeries] },
      ];
    } else {
      if (this.timeSeriesData && this.timeSeriesData.length) {
        const seriesWithMeanValuesMap = new Map();
        this.timeSeriesData.forEach((seriesData) => {
          const totalOfBasePeakDifference =
            seriesData.basePeakPercentage?.reduce((acc, curr) => {
              acc += curr?.base * 100 - curr?.peak * 100;

              return acc;
            }, 0);
          seriesWithMeanValuesMap.set(
            seriesData.name,
            totalOfBasePeakDifference / seriesData.basePeakPercentage?.length
          );
        });

        this.chartSeries = [...this.timeSeriesData]
          .sort(
            (prevValue, nextValue) =>
              seriesWithMeanValuesMap.get(prevValue.name) -
              seriesWithMeanValuesMap.get(nextValue.name)
          )
          .map((seriesData, index) => ({
            seriesId: seriesData.id,
            name: seriesData.name,
            data: seriesData.data,
            zIndex: index,
            visible: true,
          }));
      } else if (this.seriesDetails.length > 1) {
        this.chartSeries = this.seriesDetails;
      } else {
        this.cloneOfChartSeries = [...this.seriesDetails[0].data];
        this.chartSeries = [
          { ...this.seriesDetails[0], data: [...this.cloneOfChartSeries] },
        ];
      }
    }
    this.setChart();
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  watch: {
    redrawChart() {
      // Redraw chart
      if (this.redrawChart) this.redraw();
    },

    // xAxisPlotBands(newValue) {
    //   this.updateChartWithPlotBands(newValue);
    // },
  },
};
</script>

<style scoped></style>
